/**
 * PasswordStrengthMeter — autenticazione Phase 5 (IMPL-P5-008).
 *
 * Renders a 4-segment bar + a checklist mirroring the password policy
 * (UC-005 BR-1: ≥10 chars, uppercase, lowercase, digit).
 *
 * Accessibility:
 *  - Wrapping `<div>` is `role="status"` `aria-live="polite"` `aria-atomic`
 *    so SR users get a single announcement when the score changes.
 *  - Per-rule entries use `Check` / `X` Lucide icons with `aria-hidden`,
 *    plus visible label text. SR-only sentence is provided via the live
 *    region content.
 *  - Visual segments use CSS variables (no hardcoded colors).
 */
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PasswordStrengthMeterProps {
  password: string;
  /** Optional id so callers can reference this from `aria-describedby`. */
  id?: string;
}

interface Rule {
  key: 'len' | 'upper' | 'lower' | 'digit';
  label: string;
  test: (pwd: string) => boolean;
}

const RULES: Rule[] = [
  { key: 'len', label: 'Almeno 10 caratteri', test: (p) => p.length >= 10 },
  { key: 'upper', label: 'Una lettera maiuscola', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'Una lettera minuscola', test: (p) => /[a-z]/.test(p) },
  { key: 'digit', label: 'Un numero', test: (p) => /\d/.test(p) },
];

function scoreLabel(score: number): string {
  if (score === 0) return 'Inserisci una password';
  if (score <= 1) return 'Password debole';
  if (score === 2) return 'Password sufficiente';
  if (score === 3) return 'Password buona';
  return 'Password robusta';
}

export function PasswordStrengthMeter({ password, id }: PasswordStrengthMeterProps) {
  const results = RULES.map((rule) => ({ ...rule, ok: rule.test(password) }));
  const score = results.filter((r) => r.ok).length;

  return (
    <div
      id={id}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="space-y-2"
    >
      <div
        className="grid grid-cols-4 gap-1.5"
        aria-hidden="true"
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={cn(
              'h-1.5 rounded-full bg-muted transition-colors',
              i < score && score <= 1 && 'bg-destructive',
              i < score && score === 2 && 'bg-amber-500',
              i < score && score === 3 && 'bg-amber-400',
              i < score && score === 4 && 'bg-primary',
            )}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{scoreLabel(score)}</p>
      <ul className="space-y-1 text-xs">
        {results.map((rule) => (
          <li key={rule.key} className="flex items-center gap-2">
            {rule.ok ? (
              <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            ) : (
              <X className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            )}
            <span className={cn(rule.ok ? 'text-foreground' : 'text-muted-foreground')}>
              {rule.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
